import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Card, Typography, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const { Title, Paragraph } = Typography;

export const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const guestLoginInitiated = useRef(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleGuestLogin = async () => {
      const searchParams = new URLSearchParams(location.search);
      const user = searchParams.get('user');
      const password = searchParams.get('password');

      if (user === 'guest' && password && !guestLoginInitiated.current) {
        guestLoginInitiated.current = true;
        setLoading(true);
        try {
          const result = await login('guest@example.com', password);
          if (result.success) {
            message.success('Guest login successful!');
            const nextUrl = searchParams.get('next');
            const redirectUrl = searchParams.get('redirect');
            navigate((nextUrl || (redirectUrl ? `/${redirectUrl}`: null)) || '/dashboard');
          } else {
            message.error(result.message || 'Guest login failed. Please check your credentials.');
            navigate('/login');
          }
        } catch (error) {
          message.error('An unexpected error occurred during guest login.');
          navigate('/login');
        } finally {
          setLoading(false);
        }
      } else if (user || password) {
        // If there are any login parameters but not for guest, redirect to the clean login page.
        navigate('/login', { replace: true });
      }
    };

    handleGuestLogin();
  }, [location.search, login, navigate]);

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const result = await login(values.email.trim(), values.password);
      if (result.success) {
        message.success('Login successful!');
        const searchParams = new URLSearchParams(location.search);
        const nextUrl = searchParams.get('next');
        const redirectUrl = searchParams.get('redirect');
        navigate((nextUrl || (redirectUrl ? `/${redirectUrl}`: null)) || '/dashboard');
      } else {
        message.error(result.message || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      message.error('An unexpected error occurred during login.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '80vh',
      padding: '20px'
    }}>
      <Card style={{ width: '100%', maxWidth: '400px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={2}>Sign In</Title>
          <Paragraph>Welcome back to Gudu Portal</Paragraph>
        </div>

        <Form
          name="login"
          onFinish={onFinish}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please input your email address!' },
              { type: 'email', message: 'Please enter a valid email address!' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Enter your email address" 
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please input your password!' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter your password"
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              style={{ width: '100%' }}
            >
              Sign In
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center' }}>
          <Paragraph>
            Don't have an account? <Link to="/register">Sign up now</Link>
          </Paragraph>
        </div>
      </Card>
    </div>
  );
}; 